import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Clock, Users, Timer, FileDown, Layers, AlertTriangle } from "lucide-react";
import { FaseBadge } from "@/components/FaseBadge";
import { UndoBanner } from "@/components/UndoBanner";
import { RemovedCategoriesPanel } from "@/components/RemovedCategoriesPanel";

const FORMATO_LABEL = {
  direto: "Direto — cada categoria faz prévia e final antes da próxima",
  previa_final: "Prévia e Final — todas as prévias → intervalo → todas as finais",
};

export default function Schedule() {
  const { currentId, current } = useCompetition();
  const [startTime, setStartTime] = useState("18:00");
  const [intervalo, setIntervalo] = useState(0);
  const [data, setData] = useState(null);
  const [undo, setUndo] = useState(null); // { token, removed: [...] }
  const [notice, setNotice] = useState("");
  const [removed, setRemoved] = useState({ items: [], available_until: null });
  const [restoringId, setRestoringId] = useState(null);

  const loadRemoved = async () => {
    if (!currentId) return;
    try {
      const { data } = await api.get(`/competitions/${currentId}/categories/removed`);
      setRemoved(data);
    } catch {
      setRemoved({ items: [], available_until: null });
    }
  };

  const load = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/competitions/${currentId}/schedule`, {
      params: { start_time: startTime, intervalo_min: intervalo },
    });
    setData(data);
  };

  useEffect(() => {
    load();
    loadRemoved();
    setUndo(null);
    setNotice("");
    // eslint-disable-next-line
  }, [currentId, startTime, intervalo]);

  const totalHours = data ? Math.floor(data.total_minutes / 60) : 0;
  const totalMins = data ? data.total_minutes % 60 : 0;
  const totalAtletas =
    data?.schedule?.filter((s) => s.fase !== "final" && s.fase !== "intervalo").reduce((a, b) => a + (b.atletas || 0), 0) || 0;
  const emptyCount = new Set(
    (data?.schedule || [])
      .filter((s) => s.category_id && s.fase !== "final" && s.fase !== "intervalo" && !(s.atletas > 0))
      .map((s) => s.category_id)
  ).size;

  const pdfUrl = () =>
    `${process.env.REACT_APP_BACKEND_URL}/api/export/backstage.pdf?competition_id=${currentId}&start_time=${encodeURIComponent(
      startTime
    )}&intervalo_min=${intervalo}`;

  const printBackstage = async () => {
    if (!currentId) return;
    setNotice("");
    if (emptyCount === 0) {
      window.open(pdfUrl(), "_blank");
      return;
    }
    const plural = emptyCount > 1 ? "categorias" : "categoria";
    const ok = window.confirm(
      `Há ${emptyCount} ${plural} sem atletas inscritos. Deseja removê-la${emptyCount > 1 ? "s" : ""} do roteiro?`
    );
    if (!ok) {
      window.open(pdfUrl(), "_blank");
      return;
    }
    const win = window.open("", "_blank");
    try {
      const { data: res } = await api.post(`/competitions/${currentId}/categories/remove-empty`);
      await Promise.all([load(), loadRemoved()]);
      if (res.undo_token) setUndo({ token: res.undo_token, removed: res.removed });
      if (win) win.location = pdfUrl();
      else window.open(pdfUrl(), "_blank");
    } catch (e) {
      if (win) win.close();
      setNotice(extractErrorMessage(e));
    }
  };

  const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;

  const undoRemoval = async () => {
    if (!undo) return;
    try {
      const { data: res } = await api.post(`/competitions/${currentId}/categories/restore/${undo.token}`);
      setUndo(null);
      await Promise.all([load(), loadRemoved()]);
      setNotice(`${plural(res.restored, "categoria restaurada", "categorias restauradas")} na posição original.`);
    } catch (e) {
      setNotice(extractErrorMessage(e));
    }
  };

  const restoreOne = async (item) => {
    setRestoringId(item.id);
    setNotice("");
    try {
      const { data: res } = await api.post(`/competitions/${currentId}/categories/restore-one/${item.id}`);
      await Promise.all([load(), loadRemoved()]);
      setNotice(`Categoria "${res.nome}" restaurada na posição original.`);
      if (undo && undo.removed.length === 1 && undo.removed[0].id === item.id) setUndo(null);
    } catch (e) {
      setNotice(extractErrorMessage(e));
    } finally {
      setRestoringId(null);
    }
  };

  const restoreAll = async () => {
    const tokens = [...new Set(removed.items.map((i) => i.token))];
    setRestoringId("all");
    setNotice("");
    try {
      let total = 0;
      for (const t of tokens) {
        const { data: res } = await api.post(`/competitions/${currentId}/categories/restore/${t}`);
        total += res.restored || 0;
      }
      setUndo(null);
      await Promise.all([load(), loadRemoved()]);
      setNotice(`${plural(total, "categoria restaurada", "categorias restauradas")} na posição original.`);
    } catch (e) {
      setNotice(extractErrorMessage(e));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Cronograma</div>
          <h1 className="font-display text-5xl font-black tracking-tight">ROTEIRO</h1>
          {emptyCount > 0 && (
            <span
              data-testid="schedule-empty-badge"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 border border-[#FF9F0A]/50 text-[#FF9F0A] bg-[#FF9F0A]/10"
              title="Categorias sem nenhum atleta inscrito"
            >
              <AlertTriangle size={12} /> {emptyCount} {emptyCount > 1 ? "categorias vazias" : "categoria vazia"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="backstage-pdf"
            className="btn-primary flex items-center gap-2"
            onClick={printBackstage}
            disabled={!currentId}
            title="Imprimir programa completo com todas as categorias e atletas listados"
          >
            <FileDown size={16} /> Programa Backstage (PDF)
          </button>
          <CompetitionSelector />
        </div>
      </div>

      {undo && (
        <UndoBanner
          testId="schedule-undo"
          message={`${undo.removed.length} ${undo.removed.length > 1 ? "categorias vazias removidas" : "categoria vazia removida"} do roteiro e numeração atualizada.`}
          onUndo={undoRemoval}
          onExpire={() => setUndo(null)}
        />
      )}
      {notice && (
        <div className="card p-3 mb-4 text-sm text-[#C9A84C]" data-testid="schedule-notice">
          {notice}
        </div>
      )}

      <RemovedCategoriesPanel
        items={removed.items}
        availableUntil={removed.available_until}
        onRestore={restoreOne}
        onRestoreAll={restoreAll}
        busyId={restoringId}
      />

      <div className="card p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="overline block mb-2">Início do evento</label>
            <input
              data-testid="schedule-start"
              type="time"
              className="input-field"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div>
            <label className="overline block mb-2">Intervalo entre categorias (min)</label>
            <input
              data-testid="schedule-interval"
              type="number"
              min={0}
              className="input-field"
              value={intervalo}
              onChange={(e) => setIntervalo(parseInt(e.target.value || 0))}
            />
          </div>
          <div className="stat-card">
            <div className="overline mb-1 flex items-center gap-1"><Users size={12} /> Total Atletas</div>
            <div className="stat-number" style={{ fontSize: "2rem" }}>{totalAtletas}</div>
          </div>
          <div className="stat-card">
            <div className="overline mb-1 flex items-center gap-1"><Timer size={12} /> Duração Total</div>
            <div className="stat-number" style={{ fontSize: "2rem" }}>
              {totalHours}h{String(totalMins).padStart(2, "0")}
            </div>
          </div>
        </div>
        <div className="text-xs text-[var(--text-secondary)] mt-3 flex flex-wrap gap-x-6 gap-y-1">
          <span data-testid="schedule-rules">
            {data?.roteiro_config
              ? `Prévia por faixa (${data.roteiro_config.faixas_previa
                  .map((f, i, arr) => `${i === 0 ? 1 : arr[i - 1].max_atletas + 1}–${f.max_atletas}: ${f.minutos}′`)
                  .join(" · ")}; acima +1′/atleta) · Final = até ${data.roteiro_config.classificados_final} classificados × ${data.roteiro_config.min_por_atleta_final}′ · 0 atletas = 0′. Ajuste em Competições → Tempos do roteiro.`
              : "Duração calculada pelo nº de inscritos."}
          </span>
          {data?.formato && (
            <span data-testid="schedule-formato" className="inline-flex items-center gap-1 text-[#C9A84C]">
              <Layers size={11} /> Formato: {FORMATO_LABEL[data.formato] || data.formato}
            </span>
          )}
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="table-dense">
          <thead>
            <tr>
              <th className="w-14">#</th>
              <th className="w-24">
                <Clock size={13} className="inline mr-1" /> Início
              </th>
              <th className="w-24">Fim</th>
              <th>Categoria</th>
              <th className="text-center">Atletas</th>
              <th className="text-center">Duração</th>
            </tr>
          </thead>
          <tbody>
            {(data?.schedule || []).map((s, i) => (
              <tr
                key={s.key || s.category_id}
                data-testid={`schedule-row-${i}`}
                className={s.fase === "intervalo" ? "bg-white/5" : ""}
              >
                <td className="tabular-nums font-display font-bold">{s.ordem ?? "—"}</td>
                <td className="tabular-nums font-display font-bold text-lg">{s.inicio}</td>
                <td className="tabular-nums text-[var(--text-secondary)]">{s.fim}</td>
                <td>
                  <span className={s.fase === "intervalo" ? "font-black tracking-widest" : ""}>{s.categoria}</span>
                  <FaseBadge fase={s.fase} testId={`schedule-fase-${i}`} />
                </td>
                <td className="text-center tabular-nums">{s.fase === "intervalo" ? "—" : s.atletas}</td>
                <td className="text-center tabular-nums">
                  {s.duracao_min} min
                  {s.detalhe && (
                    <div className="text-[10px] text-[var(--text-secondary)]" data-testid={`schedule-detalhe-${i}`}>{s.detalhe}</div>
                  )}
                </td>
              </tr>
            ))}
            {(!data || data.schedule.length === 0) && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-[var(--text-secondary)]">
                  Nenhuma categoria cadastrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
